import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

interface EntitySubtitleTranslation {
  key: string;
  params: Record<string, number>;
}

@Component({
  selector: 'app-kiosk-stepper-navigation-atom',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex w-full items-start justify-between py-4">
      <app-button-atom
        size="M"
        [label]="'declinable-product-stepper.previous' | translate"
        color="primary"
        [icon]="'icon-arrow-down-s'"
        [iconRotation]="'180'"
        [type]="'kiosk-primary'"
        [overrideBgColor]="color()"
        [overrideTextColor]="accentColor()"
        (click)="previousStep.emit()"
        thickSize="M"
        [full]="false"
      ></app-button-atom>
      <div
        class="flex flex-col gap-2 text-center font-bold"
        [style.color]="textColor() | contentBuilderColor"
      >
        <p class="kiosk-xl:text-4xl text-3xl">
          {{ categoryName() }}
        </p>
        @if (entitySubtitle(); as sub) {
          <p class="text-2xl">{{ sub.key | translate: sub.params }}</p>
        }
      </div>
      <app-button-atom
        size="M"
        [label]="nextButtonText() | translate"
        color="primary"
        [icon]="'icon-arrow-down-s'"
        [type]="'kiosk-primary'"
        [overrideBgColor]="color()"
        [overrideTextColor]="accentColor()"
        [disabled]="!isNextButtonEnable()"
        (click)="nextStep.emit()"
        [iconOnRight]="true"
        thickSize="M"
        [full]="false"
        [class.invisible]="hideNextButton()"
      ></app-button-atom>
    </div>
  `,
  standalone: false,
})
export class KioskStepperNavigationAtomComponent {
  public readonly categoryName = input<string>();

  public readonly count = input<number>();

  public readonly minStepCount = input<number>();
  public readonly maxStepCount = input<number>();

  public readonly isPreviousButtonEnable = input<boolean>();
  public readonly isNextButtonEnable = input<boolean>();
  public readonly hideNextButton = input<boolean>(false);

  public readonly hasNextStep = input<boolean>();

  public readonly color = input<IContentBuilderFieldColor>();
  public readonly accentColor = input<IContentBuilderFieldColor>();
  public readonly textColor = input<IContentBuilderFieldColor>();

  public readonly previousStep = output<void>();
  public readonly nextStep = output<void>();

  protected readonly nextButtonText = computed(() => {
    if (!this.hasNextStep()) {
      return 'declinable-product-stepper.add';
    }
    if (this.count() === 0 && this.isNextButtonEnable()) {
      return 'declinable-product-stepper.no-thank-you';
    }
    return 'declinable-product-stepper.next';
  });

  /** Subtitle under category name: min/max selection rules, translated via i18n params. */
  protected readonly entitySubtitle = computed((): EntitySubtitleTranslation | null => {
    const min = this.minStepCount();
    const max = this.maxStepCount();
    const prefix = 'declinable-product-stepper';

    if (!min && max && Number.isFinite(max)) {
      return { key: `${prefix}.entity-subtitle-up-to-max`, params: { max } };
    }
    if (min && (!max || !Number.isFinite(max))) {
      return { key: `${prefix}.entity-subtitle-at-least-min`, params: { min } };
    }
    if (min && max && Number.isFinite(max) && min === max) {
      return { key: `${prefix}.entity-subtitle-exact`, params: { count: max } };
    }
    if (min && max && Number.isFinite(max)) {
      return { key: `${prefix}.entity-subtitle-range`, params: { min, max } };
    }
    return null;
  });
}
